/**
 * Campaign Matcher Utilities
 *
 * Functions for determining which campaign should be active
 * based on current time, date range, schedules, and priority.
 */

import { Campaign } from '../types';
import { isScheduleActive } from './scheduleParser';
import { matchesWeather } from './weatherConditionMatcher';
import { weatherStore, type WeatherData } from '~/stores/weather.store';

/**
 * Checks if the current date is within a campaign's date range.
 * Uses date-only comparison (ignores time component) to ensure
 * consistent behavior across timezones.
 *
 * @param campaign - Campaign to check
 * @param now - Current date (defaults to now)
 * @returns true if within date range
 */
export function isWithinDateRange(
  campaign: Campaign,
  now: Date = new Date(),
): boolean {
  // Parse dates and normalize to date-only comparison
  // startDate and endDate are YYYY-MM-DD strings, so we parse them as local dates
  const [startYear, startMonth, startDay] = campaign.startDate
    .split('-')
    .map(Number);
  const [endYear, endMonth, endDay] = campaign.endDate.split('-').map(Number);

  // Create dates at start/end of day in local timezone
  const startDate = new Date(startYear, startMonth - 1, startDay, 0, 0, 0, 0);
  const endDate = new Date(endYear, endMonth - 1, endDay, 23, 59, 59, 999);

  return now >= startDate && now <= endDate;
}

/**
 * Checks if a campaign is currently active.
 *
 * A campaign is active when:
 * 1. Its status is 'active'
 * 2. Current date is within startDate and endDate
 * 3. At least one of its schedules matches current time/day
 *
 * @param campaign - Campaign to check
 * @param now - Current date (defaults to now)
 * @returns true if campaign is active
 */
export function isCampaignActive(
  campaign: Campaign,
  now: Date = new Date(),
  weather: WeatherData | null = weatherStore.current,
): boolean {
  // Check status
  if (campaign.status !== 'active') {
    return false;
  }

  // Check date range
  if (!isWithinDateRange(campaign, now)) {
    return false;
  }

  // Fase 2.0 — condição de clima: filtro ADICIONAL (AND), NÃO substitui os
  // filtros de horário/dia/validade (guardrail regras-temporais-player.md).
  // Sem regra de clima → não interfere. Com regra → tem que bater.
  if (!matchesWeather(campaign.weatherCondition, weather)) {
    return false;
  }

  // If no schedules defined, campaign is always active during date range
  if (campaign.schedule.length === 0) {
    return true;
  }

  // Check if any schedule matches current time
  return campaign.schedule.some((schedule) => isScheduleActive(schedule, now));
}

/**
 * Filters campaigns to only those currently active.
 *
 * @param campaigns - Array of campaigns to filter
 * @param now - Current date (defaults to now)
 * @returns Array of active campaigns
 */
export function getActiveCampaigns(
  campaigns: Campaign[],
  now: Date = new Date(),
): Campaign[] {
  return campaigns.filter((campaign) => isCampaignActive(campaign, now));
}

/**
 * Selects the highest priority active campaign.
 *
 * Selection logic:
 * 1. Filter to only active campaigns
 * 2. Sort by priority (ascending - 0 is highest priority)
 * 3. Return the first one (highest priority)
 *
 * @param campaigns - Array of all campaigns
 * @param now - Current date (defaults to now)
 * @returns Highest priority active campaign, or null if none active
 */
export function getActiveCampaign(
  campaigns: Campaign[],
  now: Date = new Date(),
): Campaign | null {
  const activeCampaigns = getActiveCampaigns(campaigns, now);

  if (activeCampaigns.length === 0) {
    return null;
  }

  // Sort by priority ascending (lower number = higher priority, 0 = maximum)
  const sorted = [...activeCampaigns].sort((a, b) => a.priority - b.priority);

  return sorted[0];
}

/**
 * Returns ALL active campaigns sorted by priority.
 * Used for sequential rotation through multiple campaigns.
 *
 * @param campaigns - Array of all campaigns
 * @param now - Current date (defaults to now)
 * @returns All active campaigns sorted by priority (0 = highest priority first)
 */
export function getAllActiveCampaignsSorted(
  campaigns: Campaign[],
  now: Date = new Date(),
): Campaign[] {
  const activeCampaigns = getActiveCampaigns(campaigns, now);

  // Sort by priority ascending (0 = máxima prioridade)
  return [...activeCampaigns].sort((a, b) => a.priority - b.priority);
}

/**
 * Gets all campaigns that will be active at a specific time.
 * Useful for previewing what will play at a future time.
 *
 * @param campaigns - Array of all campaigns
 * @param targetTime - Time to check
 * @returns Array of campaigns active at that time
 */
export function getCampaignsAtTime(
  campaigns: Campaign[],
  targetTime: Date,
): Campaign[] {
  return getActiveCampaigns(campaigns, targetTime);
}

/**
 * Calculates when the active campaign will change.
 * Returns the soonest time when either:
 * - Current campaign's schedule ends
 * - A higher priority campaign becomes active
 *
 * @param campaigns - Array of all campaigns
 * @param activeCampaign - Currently active campaign
 * @param now - Current date (defaults to now)
 * @returns Date when campaign will change, or null if won't change today
 */
export function getNextCampaignChange(
  campaigns: Campaign[],
  activeCampaign: Campaign | null,
  _now: Date = new Date(),
): Date | null {
  // For simplicity, we re-evaluate every minute
  // A more sophisticated implementation could calculate exact change times
  return null;
}

/**
 * Validates a campaign's structure.
 *
 * @param campaign - Campaign to validate
 * @returns Array of validation error messages (empty if valid)
 */
export function validateCampaign(campaign: Campaign): string[] {
  const errors: string[] = [];

  if (!campaign.id) {
    errors.push('Campaign must have an id');
  }

  if (!campaign.name) {
    errors.push('Campaign must have a name');
  }

  if (!campaign.startDate) {
    errors.push('Campaign must have a startDate');
  }

  if (!campaign.endDate) {
    errors.push('Campaign must have an endDate');
  }

  if (campaign.startDate && campaign.endDate) {
    const start = new Date(campaign.startDate);
    const end = new Date(campaign.endDate);
    if (start > end) {
      errors.push('startDate must be before endDate');
    }
  }

  if (typeof campaign.priority !== 'number') {
    errors.push('Campaign must have a numeric priority');
  }

  if (!Array.isArray(campaign.playlist)) {
    errors.push('Campaign must have a playlist array');
  }

  if (!Array.isArray(campaign.schedule)) {
    errors.push('Campaign must have a schedule array');
    return errors; // Return early - cannot validate schedules if not an array
  }

  // Validate schedules
  campaign.schedule.forEach((schedule, index) => {
    if (!schedule.id) {
      errors.push(`Schedule ${index} must have an id`);
    }

    if (!Array.isArray(schedule.daysOfWeek)) {
      errors.push(`Schedule ${index} must have daysOfWeek array`);
    } else {
      const invalidDays = schedule.daysOfWeek.filter((d) => d < 0 || d > 6);
      if (invalidDays.length > 0) {
        errors.push(
          `Schedule ${index} has invalid days: ${invalidDays.join(', ')}`,
        );
      }
    }

    if (!schedule.startTime?.match(/^\d{1,2}:\d{2}$/)) {
      errors.push(`Schedule ${index} has invalid startTime format`);
    }

    if (!schedule.endTime?.match(/^\d{1,2}:\d{2}$/)) {
      errors.push(`Schedule ${index} has invalid endTime format`);
    }

    if (!schedule.timezone) {
      errors.push(`Schedule ${index} must have a timezone`);
    }
  });

  return errors;
}
